import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  Injectable,
  Provider,
  Self
} from '@angular/core';
import {
  AbstractTuiDialogService,
  TUI_DIALOGS,
  TUI_IS_MOBILE,
  TuiDestroyService,
  TuiDialog
} from '@taiga-ui/cdk';
import {
  TuiAnimationOptions,
  tuiFadeIn,
  tuiSlideInTop,
  TUI_ANIMATIONS_DURATION,
  TUI_CLOSE_WORD,
  TuiDialogCloseService,
  TUI_DIALOGS_CLOSE
} from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT, PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { isObservable, merge, Observable, of, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { marker as extract } from '@biesbjerg/ngx-translate-extract-marker';

export interface CustomDialogOptions<I> {
  readonly required?: boolean;
  readonly dismissible?: Observable<boolean> | boolean;
  readonly skipCloseStream?: boolean;
  readonly headingAsBody?: boolean;
  readonly size: 'page' | 'default';
  readonly closeable: Observable<boolean> | boolean;
  readonly heading: string;
  readonly customButtons: boolean;
  readonly withoutHeading: boolean;
  readonly buttons?: readonly [
    { title: string; appearance: string },
    { title: string; appearance: string }
  ];
  readonly data: I;
}

const REQUIRED_ERROR = new Error('Required dialog was dismissed');

@Component({
  selector: 'app-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [tuiSlideInTop, tuiFadeIn],
  providers: [TuiDestroyService, TuiDialogCloseService]
})
export class CustomDialogComponent<O, I> {
  private readonly animation = {
    value: '',
    params: {
      start: '40px',
      duration: this.duration
    }
  } as const;

  private readonly fullscreenAnimation = {
    value: '',
    params: {
      start: '100vh',
      duration: this.duration
    }
  } as const;

  readonly close$ = new Subject();

  constructor(
    @Inject(TUI_ANIMATIONS_DURATION) public readonly duration: number,
    @Inject(TUI_IS_MOBILE) private readonly isMobile: boolean,
    @Inject(POLYMORPHEUS_CONTEXT)
    readonly context: TuiDialog<CustomDialogOptions<I>, O>,
    @Inject(TuiDestroyService) @Self() destroy$: Observable<void>,
    @Inject(TuiDialogCloseService) dialogClose$: Observable<unknown>,
    @Inject(TUI_DIALOGS_CLOSE) close$: Observable<unknown>,
    @Inject(TUI_CLOSE_WORD) readonly closeWord$: Observable<string>
  ) {
    merge(
      this.close$.pipe(
        switchMap(() =>
          isObservable(context.closeable) ? context.closeable : of(context.closeable)
        ),
        filter(Boolean)
      ),
      dialogClose$.pipe(
        switchMap(() =>
          isObservable(context.dismissible) ? context.dismissible : of(context.dismissible)
        ),
        filter(Boolean)
      ),
      close$.pipe(filter(() => !context?.skipCloseStream))
    )
      .pipe(takeUntil(destroy$))
      .subscribe(() => {
        this.close(false);
      });
  }

  @HostBinding('attr.data-size')
  get size(): 'page' | 'default' {
    return this.context.size;
  }

  @HostBinding('@tuiSlideInTop')
  @HostBinding('@tuiFadeIn')
  get slideInTop(): TuiAnimationOptions {
    return this.size === 'page' ? this.fullscreenAnimation : this.animation;
  }

  close(response: any) {
    if (!response && this.context.required) {
      this.context.$implicit.error(REQUIRED_ERROR);
    } else {
      this.context.completeWith(response);
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class CustomDialogService extends AbstractTuiDialogService<CustomDialogOptions<any>> {
  readonly defaultOptions = {
    required: false,
    dismissible: true,
    size: 'default',
    closeable: false,
    heading: extract('Are you sure?'),
    customButtons: false,
    skipCloseStream: false,
    withoutHeading: false,
    buttons: [
      { title: extract('Cancel'), appearance: 'aitarget-secondary' },
      { title: extract('Delete'), appearance: 'danger-primary' }
    ],
    data: undefined
  } as const;
  readonly component = new PolymorpheusComponent(CustomDialogComponent);
}

export const CUSTOM_DIALOG_PROVIDER: Provider = {
  provide: TUI_DIALOGS,
  useExisting: CustomDialogService,
  multi: true
};
